import { useState } from "react";
import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const PROJECTS = [
  {
    title: "Newman Injury Law",
    tag: "AI Commercial Campaign",
    youtubeId: "pIrpbPLO6zU",
  },
  {
    title: "Diamond Club Matchmaking",
    tag: "Brand Film + Website",
    youtubeId: "4yFrl_5xk0w",
  },
  {
    title: "King Fox Cleaners",
    tag: "AI Commercial",
    youtubeId: "Su8trRf75uk",
  },
  {
    title: "TEG Construction",
    tag: "Brand Content",
    youtubeId: "5REC4oKo8No",
  },
];

const thumbUrl = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
const thumbHi = (id) => `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;

export default function Work() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="work"
      data-testid="work-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <span
              className="text-xs uppercase tracking-[0.22em] text-[#00d4ff]/90"
              data-testid="work-eyebrow"
            >
              Our Work
            </span>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="font-display mt-3 text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight"
              data-testid="work-headline"
            >
              Selected Work
            </motion.h2>
          </div>
          <p className="text-zinc-400 text-base md:text-lg max-w-md">
            A snapshot of recent campaigns, brand films, and full-stack
            digital experiences delivered by AI Volta.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {PROJECTS.map((p, i) => (
            <motion.button
              type="button"
              key={p.title}
              onClick={() => setActive(p)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: (i % 4) * 0.08, duration: 0.55 }}
              data-testid={`work-card-${i}`}
              className="group relative overflow-hidden rounded-xl border border-white/10 bg-[#13131a] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00d4ff]/60"
              aria-label={`Play ${p.title} video`}
            >
              <div className="relative aspect-[4/5] w-full overflow-hidden">
                <img
                  src={thumbHi(p.youtubeId)}
                  onError={(e) => {
                    if (!e.currentTarget.dataset.fallback) {
                      e.currentTarget.dataset.fallback = "1";
                      e.currentTarget.src = thumbUrl(p.youtubeId);
                    }
                  }}
                  onLoad={(e) => {
                    // maxresdefault returns a tiny 120x90 placeholder when unavailable
                    if (
                      !e.currentTarget.dataset.fallback &&
                      e.currentTarget.naturalWidth <= 200
                    ) {
                      e.currentTarget.dataset.fallback = "1";
                      e.currentTarget.src = thumbUrl(p.youtubeId);
                    }
                  }}
                  alt={p.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/40 to-transparent" />

                <div className="absolute inset-0 flex items-center justify-center opacity-90 group-hover:opacity-100 transition">
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-white/30 bg-black/40 backdrop-blur-sm transition-transform group-hover:scale-110 group-hover:border-[#00d4ff]/70 group-hover:bg-[#00d4ff]/10">
                    <Play className="h-5 w-5 text-white" fill="white" />
                  </span>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-display text-lg font-semibold text-white">
                  {p.title}
                </h3>
                <p className="text-xs uppercase tracking-wider text-[#9be8ff]/80 mt-1">
                  {p.tag}
                </p>
              </div>
            </motion.button>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <Button
            asChild
            variant="outline"
            data-testid="work-cta-more"
            className="h-11 px-6 rounded-full bg-transparent border border-white/15 text-white hover:bg-white/5 hover:text-white"
          >
            <a href="#contact">
              View More Work
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="work-video-dialog"
          className="max-w-4xl w-[95vw] p-0 bg-[#0a0a0f] border border-white/10 overflow-hidden gap-0 [&>button]:bg-black/60 [&>button]:text-white [&>button]:h-9 [&>button]:w-9 [&>button]:rounded-full [&>button]:border [&>button]:border-white/15 [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:opacity-100 [&>button]:top-3 [&>button]:right-3"
        >
          <DialogTitle className="sr-only">
            {active?.title || "Project video"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {active?.tag || ""}
          </DialogDescription>
          {active && (
            <>
              <div className="relative w-full aspect-video bg-black">
                <iframe
                  key={active.youtubeId}
                  data-testid="work-video-iframe"
                  src={`https://www.youtube-nocookie.com/embed/${active.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                  title={active.title}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 h-full w-full"
                />
              </div>
              <div className="p-5 md:p-6 border-t border-white/10 flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#9be8ff]/80">
                    {active.tag}
                  </div>
                  <h3 className="font-display mt-1 text-xl md:text-2xl font-semibold text-white">
                    {active.title}
                  </h3>
                </div>
                <a
                  href={`https://youtu.be/${active.youtubeId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="work-video-youtube-link"
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 h-10 text-sm text-zinc-200 hover:text-white hover:border-[#00d4ff]/40 hover:bg-[#00d4ff]/5 transition-colors"
                >
                  Watch on YouTube
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
